import { ApplicationConfig } from '@angular/core';
import { provideRouter, withEnabledBlockingInitialNavigation, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { appRoutes } from './app.routes';

import Aura from '@primeuix/themes/aura';
import { providePrimeNG } from 'primeng/config';

import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import {
    includeBearerTokenInterceptor,
    INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
    createInterceptorCondition,
    IncludeBearerTokenCondition,
} from 'keycloak-angular';

import { environment } from './environments/environment';
import { localAuthEnabled } from './services/auth/local-auth';
import { customHeadersInterceptor } from './services/interceptors/custom-headers-interceptor';
import { loadingInterceptor } from './services/interceptors/loading-interceptor';
import { errorInterceptor } from './services/interceptors/error-interceptor';
import {ConfirmationService, MessageService} from "primeng/api";
import {provideAnimations} from "@angular/platform-browser/animations";
import {AppThemePreset} from "@/layout/theme/theme.preset";

// ---- helpers --------------------------------------------------------------
function originOf(u: string): string { try { return new URL(u).origin; } catch { return u; } }
function escapeRegex(s: string) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

const KC_ORIGIN = originOf(environment.loginUrl); // dev: http://localhost:8989
const API_ORIGIN = originOf(environment.baseUrl); // dev: http://localhost:4200
const API_PREFIX = '/api';

const conditions = [
    createInterceptorCondition<IncludeBearerTokenCondition>({
        urlPattern: new RegExp(`^${escapeRegex(KC_ORIGIN)}`, 'i'),
        bearerPrefix: 'Bearer',
    }),
    createInterceptorCondition<IncludeBearerTokenCondition>({
        urlPattern: new RegExp(`^${escapeRegex(API_ORIGIN)}${escapeRegex(API_PREFIX)}`, 'i'),
        bearerPrefix: 'Bearer',
    }),
];

export const appConfig: ApplicationConfig = {
    providers: [
        MessageService,
        ConfirmationService,
        provideRouter(
            appRoutes,
            withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' }),
            withEnabledBlockingInitialNavigation(),
        ),

        { provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG, useValue: conditions },

        provideHttpClient(
            withFetch(),
            // ordine: bearer → headers → loading → error
            withInterceptors([
                ...(localAuthEnabled ? [] : [includeBearerTokenInterceptor]),
                customHeadersInterceptor,
                loadingInterceptor,
                errorInterceptor,
            ]),
        ),

        provideAnimations(),

        // ngx-translate v17: default/fallback + loader HTTP
        provideTranslateService({
            lang: 'it',
            fallbackLang: 'it',
            loader: provideTranslateHttpLoader({
                // cartella delle traduzioni
                prefix: '/assets/i18n/',
                // estensione file
                suffix: '.json',
            }),
        }),

        // PrimeNG: tema + traduzione italiana globale (se ce l’hai)
        providePrimeNG({ theme: { preset: AppThemePreset, options: { darkModeSelector: '.app-dark' } } }),
    ],
};
